import gql from "graphql-tag";
import { BuildQueryResult } from "ra-data-graphql";
import { QueryTypes } from "../../constants";
import type { PatientAccess } from "./buildQueryProjectPatient";

/* eslint-disable @typescript-eslint/no-explicit-any */

export const COMPLIANCE_METRIC_CATEGORIES = [
  "TREMOR_DYSKINESIA",
  "MOBILITY",
  "SLEEP",
  "TAP_TEST"
] as const;

export type ComplianceMetricCategory =
  (typeof COMPLIANCE_METRIC_CATEGORIES)[number];

export type ComplianceConfig = {
  id: string;
  projectId: string;
  // carrotgraph ComplianceMetricCategory values, eg "tremor_dyskinesia"
  categories: string[];
  // Medication classifications, eg "patient.medication.levodopa"
  studyMedications: string[];
  complianceThresholdDays: number;
  atRiskThresholdDays: number;
  outreachGapDays: number;
  maxOutreachAttempts: number | null;
};

export type ComplianceExemptionReason =
  | "medical"
  | "personal"
  | "non_responsive";

export type ComplianceExemptionData = {
  patientId: string;
  reason: ComplianceExemptionReason;
  // ComplianceMetricCategory values, as in the config's categories
  categories: string[];
  detail: string | null;
};

// carrotgraph's maximum page size for the exemption list.
const EXEMPTION_PAGE_LIMIT = 100;

export type ComplianceProjectData = {
  id: string;
  title: string;
  organizationId: string;
  totalProjectPatientCount: number;
};

export type ComplianceOrgData = {
  id: string;
  displayName: string;
  orgTags: string[] | null;
};

export type MetricCategoryTimeRange = {
  metricCategory: string;
  endTime: number | null;
};

export type ComplianceProjectPatientData = {
  organizationId: string;
  codeName: string | null;
  // When the patient was added to the project
  createdAt: number;
  patient: {
    id: string;
    codeName: string;
    createdAt: number | null;
    appMode: {
      displayName: string;
    } | null;
    patientAccessList: {
      patientAccess: PatientAccess[];
    } | null;
  };
  metricCategoryTimeRanges: MetricCategoryTimeRange[];
};

const buildQueryCompliance = (
  fetchType: string,
  resource: string,
  params: any
): BuildQueryResult => {
  if (fetchType === QueryTypes.GET_LIST) {
    if (resource === "ProjectComplianceConfig") {
      return {
        query: gql`
          query getProjectComplianceConfigList {
            projectComplianceConfigList {
              configs {
                id
                projectId
                categories
                studyMedications
                complianceThresholdDays
                atRiskThresholdDays
                outreachGapDays
                maxOutreachAttempts
              }
            }
          }
        `,
        variables: {},
        parseResponse: (response) => {
          const configs = response.data.projectComplianceConfigList.configs;
          return { data: configs, total: configs.length };
        }
      };
    }

    // Not filtered by project: carrotgraph lists every patient's exemption.
    if (resource === "ComplianceExemption") {
      return {
        query: gql`
          query getComplianceExemptionList(
            $cursor: DateTimeIDCursor
            $limit: Int
          ) {
            complianceExemptionList(cursor: $cursor, limit: $limit) {
              exemptions {
                patientId
                reason
                categories
                detail
              }
              pageInfo {
                endCursor
              }
            }
          }
        `,
        variables: {
          cursor: params.meta?.nextPageCursor ?? null,
          limit: EXEMPTION_PAGE_LIMIT
        },
        parseResponse: (response) => {
          const { exemptions, pageInfo } =
            response.data.complianceExemptionList;
          return {
            data: exemptions,
            total: exemptions.length,
            endCursor: pageInfo?.endCursor ?? null
          };
        }
      };
    }

    if (resource === "ComplianceProjectPatient") {
      return {
        query: gql`
          query getComplianceProjectPatients(
            $projectId: ID
            $metricCategories: [MetricCategoryEnum]
            $cursorInput: CursorInput
          ) {
            project(id: $projectId) {
              id
              organizationId
              projectPatientList(cursorInput: $cursorInput) {
                projectPatients {
                  codeName
                  createdAt
                  patient {
                    id
                    codeName
                    createdAt
                    appMode {
                      displayName
                    }
                    patientAccessList {
                      patientAccess {
                        startTime
                        endTime
                        isActive
                        org {
                          id
                        }
                      }
                    }
                  }
                  metricCategoryTimeRanges(
                    metricCategories: $metricCategories
                  ) {
                    metricCategory
                    endTime
                  }
                }
                pageInfo {
                  codeNameEndCursor
                }
              }
            }
          }
        `,
        variables: {
          projectId: params.filter.projectId,
          metricCategories: COMPLIANCE_METRIC_CATEGORIES,
          cursorInput: params.meta?.nextPageCursor
            ? { codeNameCursor: params.meta.nextPageCursor }
            : null
        },
        parseResponse: (response) => {
          const { organizationId, projectPatientList } = response.data.project;
          const data = projectPatientList.projectPatients.map((pp: any) => ({
            ...pp,
            organizationId
          }));
          // Null on the last page; pages are at most 1000 patients.
          return {
            data,
            total: data.length,
            endCursor: projectPatientList.pageInfo?.codeNameEndCursor ?? null
          };
        }
      };
    }
  }

  if (fetchType === QueryTypes.GET_ONE) {
    if (resource === "ComplianceProject") {
      return {
        query: gql`
          query getComplianceProject($projectId: ID) {
            project(id: $projectId) {
              id
              title
              organizationId
              totalProjectPatientCount
            }
          }
        `,
        variables: { projectId: params.id },
        parseResponse: (response) => ({ data: response.data.project })
      };
    }

    if (resource === "ComplianceOrg") {
      return {
        query: gql`
          query getComplianceOrg($orgId: ID) {
            org(orgId: $orgId) {
              id
              displayName
              orgTags
            }
          }
        `,
        variables: { orgId: params.id },
        parseResponse: (response) => ({ data: response.data.org })
      };
    }
  }

  throw new Error(`Unsupported fetch type ${fetchType} for ${resource}`);
};

export default buildQueryCompliance;
